import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import styles from './Profile.module.css';
import LoadingSpinner from '../../components/LoadingSpinner';
import api from '../../services/api';
import ChangePasswordModal from './components/ChangePasswordModal';
import {
  UserIcon,
  BookIcon,
  TargetIcon,
  SettingsIcon,
  MessageIcon,
  InfoIcon,
  StarIcon,
  HeartIcon,
  HistoryIcon,
} from '../../components/Icons';

const Profile = () => {
  const { user: authUser, logout } = useAuth();
  const navigate = useNavigate();
  const [userStats, setUserStats] = useState({
    favoriteWords: 0,
    favoriteSentences: 0,
    readChapters: 0,
    totalWords: 0
  });
  const [loading, setLoading] = useState(true);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [copyStatus, setCopyStatus] = useState('');
  const [vipExpiresAt, setVipExpiresAt] = useState(null);
  const [showResetModal, setShowResetModal] = useState(false);

  // 调试：查看当前用户头像 URL
  useEffect(() => {
    if (authUser?.avatar) {
      // eslint-disable-next-line no-console
      console.log('[avatar] from authUser:', authUser.avatar);
    } else {
      // eslint-disable-next-line no-console
      console.log('[avatar] from authUser: <empty>');
    }
  }, [authUser]);

  // 获取用户统计数据
  useEffect(() => {
    const fetchUserStats = async () => {
      try {
        setLoading(true);
        // 这里可以添加API调用来获取用户统计数据
        // 暂时使用模拟数据
        setUserStats({
          favoriteWords: 25,
          favoriteSentences: 12,
          readChapters: 8,
          totalWords: 156
        });

        // 获取用户邀请码
        const response = await api.get('/auth/me');
        const code = response?.data?.invite_code;
        const vipExpiry = response?.data?.vip_expires_at;
        if (code) {
          setInviteCode(code);
          const url = new URL(window.location.origin);
          // 假设注册页路径为 /register（根据实际情况调整）
          const inviteUrl = `${url.origin}/register?invite=${code}`;
          setInviteLink(inviteUrl);
        } else {
          setInviteCode('');
          setInviteLink('');
        }
        if (vipExpiry) {
          setVipExpiresAt(new Date(vipExpiry));
        } else {
          setVipExpiresAt(null);
        }
      } catch (error) {
        console.error('获取用户统计失败:', error);
      } finally {
        setLoading(false);
      }
    };

    if (authUser) {
      fetchUserStats();
    }
  }, [authUser]);

  // 处理菜单项点击
  const handleMenuClick = (action) => {
    switch (action) {
      case 'feedback':
        navigate('/feedback');
        break;
      case 'about':
        navigate('/about');
        break;
      case 'settings':
        navigate('/settings');
        break;
      case 'account':
        navigate('/profile/account');
        break;
      case 'change_password':
        setShowResetModal(true);
        break;
      default:
        break;
    }
  };

  const handleCloseReset = () => {
    setShowResetModal(false);
  };

  const handleCopy = async (value, label) => {
    if (!value) return;
    if (!navigator?.clipboard) {
      setCopyStatus('浏览器不支持复制功能请使用https访问本站');
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      setCopyStatus(`${label}已复制`);
      setTimeout(() => setCopyStatus(''), 2000);
    } catch (error) {
      console.error('复制失败:', error);
      setCopyStatus('复制失败，请稍后重试');
      setTimeout(() => setCopyStatus(''), 2000);
    }
  };

  // 判断 VIP 是否仍在有效期内
  const isVipActive = React.useMemo(() => {
    if (!vipExpiresAt) return false;
    return vipExpiresAt > new Date();
  }, [vipExpiresAt]);

  // 到期时间距今超过 50 年视为终身会员
  const isLifetime = React.useMemo(() => {
    if (!vipExpiresAt) return false;
    const yearsLeft = (vipExpiresAt - new Date()) / (365.25 * 24 * 60 * 60 * 1000);
    return yearsLeft > 50;
  }, [vipExpiresAt]);

  const vipDateText = React.useMemo(() => {
    if (!vipExpiresAt) return '';
    const formatter = new Intl.DateTimeFormat('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    return formatter.format(vipExpiresAt);
  }, [vipExpiresAt]);

  if (loading) {
    return (
      <div className={styles.profileLoading}>
        <LoadingSpinner text="加载中..." />
      </div>
    );
  }

  return (
    <div className={styles.profile}>
      {/* 我的账户 */}
      <section className={styles.accountSection}>
        <div className={styles.accountCard}>
          <div className={styles.userSummary}>
            <div className={styles.userAvatar}>
              {authUser?.avatar ? (
                <img
                  src={authUser.avatar}
                  alt="avatar"
                  className={styles.userAvatarImg}
                />
              ) : (
                <UserIcon size={40} />
              )}
            </div>
            <div className={styles.userInfo}>
              <h2 className={styles.accountName}>{authUser?.username || '用户'}</h2>
              <div className={styles.vipRow}>
                {isVipActive ? (
                  <>
                    <span className={styles.vipBadge}>{isLifetime ? '终身VIP' : 'VIP'}</span>
                    <p className={styles.vipDate}>
                      {isLifetime ? '终身会员，永久有效' : `有效期至 ${vipDateText}`}
                    </p>
                  </>
                ) : vipExpiresAt ? (
                  <p className={styles.vipDate}>VIP 已于 {vipDateText} 过期</p>
                ) : (
                  <p className={styles.vipDate}>您还不是 VIP</p>
                )}
              </div>
            </div>
            {!isLifetime && (
              <button 
                type="button" 
                className={styles.renewBtn}
                onClick={() => navigate('/vip/renewal')}
              >
                {isVipActive ? '续费' : '开通VIP'}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* 邀请信息 */}
      <div className={styles.inviteCard}>
        <div className={styles.inviteTitleRow}>
          <span className={`material-symbols-outlined ${styles.inviteIcon}`}>card_giftcard</span>
          <div className={styles.inviteTexts}>
            <h3 className={styles.inviteTitle}>邀请好友，共得奖励</h3>
            <p className={styles.inviteDesc}>
              分享邀请码，您和被分享者都将免费获得
              <span className={styles.inviteHighlight}>30天 VIP</span>
              会员资格。
            </p>
          </div>
          {copyStatus && <span className={styles.copyStatus}>{copyStatus}</span>}
        </div>

        {inviteCode ? (
          <div className={styles.inviteGrid}>
            <div className={`${styles.inviteItem} ${styles.inviteItemPrimary}`}>
              <div className={styles.inviteItemText}>
                <p className={styles.inviteItemLabel}>分享邀请码</p>
                <p className={styles.inviteItemValue}>{inviteCode}</p>
              </div>
              <button
                type="button"
                className={`${styles.inviteButton} ${styles.inviteButtonPrimary}`}
                onClick={() => handleCopy(inviteCode, '邀请码')}
              >
                复制
              </button>
            </div>

            {inviteLink && (
              <div className={`${styles.inviteItem} ${styles.inviteItemSecondary}`}>
                <div className={styles.inviteItemText}>
                  <p className={styles.inviteItemLabel}>分享链接</p>
                  <p className={`${styles.inviteItemValue} ${styles.inviteLink}`}>{inviteLink}</p>
                </div>
                <button
                  type="button"
                  className={`${styles.inviteButton} ${styles.inviteButtonGhost}`}
                  onClick={() => handleCopy(inviteLink, '邀请链接')}
                >
                  链接
                </button>
              </div>
            )}
          </div>
        ) : (
          <p className={styles.invitePlaceholder}>暂未生成邀请码，请稍后重试或联系客服</p>
        )}
      </div>

      {/* 功能菜单 */}
      <div className={styles.menuSection}>
        <div className={styles.menuList}>
          <div 
            className={styles.menuItem}
            onClick={() => handleMenuClick('account')}
          >
            <div className={`${styles.menuIcon} ${styles.menuIconAccent}`}>
              <UserIcon size={24} />
            </div>
            <div className={styles.menuContent}>
              <div className={styles.menuTitle}>我的账户</div>
              <div className={styles.menuDescription}>查看账号信息与安全设置</div>
            </div>
            <div className={styles.menuArrow}>›</div>
          </div>

          <div 
            className={styles.menuItem}
            onClick={() => handleMenuClick('settings')}
          >
            <div className={`${styles.menuIcon} ${styles.menuIconAccent}`}>
              <SettingsIcon size={24} />
            </div>
            <div className={styles.menuContent}>
              <div className={styles.menuTitle}>应用设置</div>
              <div className={styles.menuDescription}>个性化设置和偏好配置</div>
            </div>
            <div className={styles.menuArrow}>›</div>
          </div>

          <div 
            className={styles.menuItem}
            onClick={() => navigate('/profile/history')}
          >
            <div className={`${styles.menuIcon} ${styles.menuIconAccent}`}>
              <HistoryIcon size={24} />
            </div>
            <div className={styles.menuContent}>
              <div className={styles.menuTitle}>阅读历史</div>
              <div className={styles.menuDescription}>查看章节阅读记录</div>
            </div>
            <div className={styles.menuArrow}>›</div>
          </div>

          <div 
            className={styles.menuItem}
            onClick={() => navigate('/voting')}
          >
            <div className={`${styles.menuIcon} ${styles.menuIconAccent}`}>
              <StarIcon size={24} />
            </div>
            <div className={styles.menuContent}>
              <div className={styles.menuTitle}>小说投票</div>
              <div className={styles.menuDescription}>为喜欢的小说投票，推荐新书</div>
            </div>
            <div className={styles.menuArrow}>›</div>
          </div>

          <div 
            className={styles.menuItem}
            onClick={() => handleMenuClick('feedback')}
          >
            <div className={`${styles.menuIcon} ${styles.menuIconAccent}`}>
              <MessageIcon size={24} />
            </div>
            <div className={styles.menuContent}>
              <div className={styles.menuTitle}>用户反馈</div>
              <div className={styles.menuDescription}>提交建议和问题反馈</div>
            </div>
            <div className={styles.menuArrow}>›</div>
          </div>

          <div 
            className={styles.menuItem}
            onClick={() => navigate('/help')}
          >
            <div className={`${styles.menuIcon} ${styles.menuIconAccent}`}>
              <BookIcon size={24} />
            </div>
            <div className={styles.menuContent}>
              <div className={styles.menuTitle}>帮助中心</div>
              <div className={styles.menuDescription}>使用指南和常见问题</div>
            </div>
            <div className={styles.menuArrow}>›</div>
          </div>

          <div 
            className={styles.menuItem}
            onClick={() => handleMenuClick('about')}
          >
            <div className={`${styles.menuIcon} ${styles.menuIconAccent}`}>
              <InfoIcon size={24} />
            </div>
            <div className={styles.menuContent}>
              <div className={styles.menuTitle}>关于我们</div>
              <div className={styles.menuDescription}>了解应用版本和团队信息</div>
            </div>
            <div className={styles.menuArrow}>›</div>
          </div>
        </div>
      </div>

      {/* 退出登录 */}
      <div className={styles.logoutSection}>
        <button 
          className={`${styles.logoutBtn} btn btn-secondary`}
          onClick={logout}
        >
          退出登录
        </button>
      </div>

      <ChangePasswordModal open={showResetModal} onClose={handleCloseReset} />
      
    </div>
  );
};

export default Profile;
